import { injectable } from 'inversify';
import BaseService from '@core/services/base';
import Config from '@core/config';
import { HttpProgressParams, ListResponse, PageInfo } from '@shared/types/services';
import { SortingType } from '@shared/constants/services';
import Axios, { AxiosInstance } from 'axios';
import HttpClient from '@core/HttpClient';
import container from '@core/di';
import { AssessmentMatrix, IAssessmentMatrix } from '@shared/models/matrix';
import { getQueries } from '@shared/utils/common';
import {
  BrainFunctionContent,
  BrainFunctionDeleteData,
  BrainFunctionUpdateData,
  BrainFunctionAddData,
} from '@shared/models/brain-function-content';
import { FamilyServiceType } from '@shared/models/family';
import {
  DateRange,
  PendingAssessment,
  PendingAssessmentStatus,
} from '@shared/models/pending-assessment';
import qs from 'qs';

export enum PendingAssessmentsRequestFilters {
  ServiceTypes = 'ServiceTypes',
  AssessmentStatuses = 'AssessmentStatuses',
  StartDate = 'StartDate',
  AssignedClinicians = 'AssignedClinicians',
}

export enum PendingAssessmentsSortingField {
  StartedSubmitted = 'StartedSubmitted',
  ClientName = 'ClientName',
  ServiceType = 'ServiceType',
  Country = 'Country',
  CompletionCount = 'CompletionCount',
  Clinician = 'Clinician',
}

export interface GetPendingAssessmentsParams {
  Page?: number;
  PageSize?: number;
  SearchKeyword?: string;
  [PendingAssessmentsRequestFilters.ServiceTypes]?: FamilyServiceType[];
  [PendingAssessmentsRequestFilters.AssessmentStatuses]?: PendingAssessmentStatus[];
  [PendingAssessmentsRequestFilters.StartDate]?: DateRange;
  [PendingAssessmentsRequestFilters.AssignedClinicians]?: number[];
  'Sort.Field'?: PendingAssessmentsSortingField;
  'Sort.Type'?: SortingType;
}

@injectable()
export default class AssessmentsService extends BaseService {
  static diToken = Symbol('assessments-service');
  private httpClient: AxiosInstance;

  private abortController = new AbortController();

  constructor() {
    super({ domainArea: 'assessments' });
    this.httpClient = container.get<HttpClient>(HttpClient.diToken).getInstance();
  }

  async getPendingList(
    params?: GetPendingAssessmentsParams,
  ): Promise<{ pageInfo: PageInfo; items: PendingAssessment[] }> {
    const { data } = await Axios.get<ListResponse<PendingAssessment>>(`${this.urlPrefix}/pending`, {
      params: params ?? this.getPendingListParams(),
      paramsSerializer: p => qs.stringify(p, { arrayFormat: 'repeat', allowDots: true }),
    });

    const { items, pageInfo } = data;

    return {
      pageInfo,
      items: items,
    };
  }

  private getPendingListParams(): GetPendingAssessmentsParams {
    const queries = getQueries();

    return {
      Page: queries.pagination?.page,
      PageSize: queries.pagination?.pageSize,
      SearchKeyword: queries.filtering?.SearchKeyword || undefined,
      [PendingAssessmentsRequestFilters.ServiceTypes]:
        queries.filtering?.[PendingAssessmentsRequestFilters.ServiceTypes] || undefined,
      [PendingAssessmentsRequestFilters.AssessmentStatuses]:
        queries.filtering?.[PendingAssessmentsRequestFilters.AssessmentStatuses] || undefined,
      [PendingAssessmentsRequestFilters.StartDate]:
        queries.filtering?.[PendingAssessmentsRequestFilters.StartDate] || undefined,
      [PendingAssessmentsRequestFilters.AssignedClinicians]:
        queries.filtering?.[PendingAssessmentsRequestFilters.AssignedClinicians] || undefined,
      'Sort.Field': queries.sorting?.field,
      'Sort.Type': queries.sorting?.type,
    };
  }

  async getMatrix() {
    const { data } = await this.httpClient.get<IAssessmentMatrix>(this.getURL('matrix'));

    return new AssessmentMatrix(data);
  }

  public cancelUpload() {
    this.abortController.abort();
  }

  updateTreatmentPlanModel(formData: FormData, progressCallback: (value: number) => void) {
    this.abortController = new AbortController();

    return this.httpClient.post(this.getURL('treatment-plan-model-template'), formData, {
      headers: {
        'content-type': 'multipart/form-data',
      },
      onUploadProgress: (progressData: HttpProgressParams) => {
        if (progressData.total) {
          progressCallback(Math.round((100 * progressData.loaded) / progressData.total));
        }
      },
      signal: this.abortController.signal,
    });
  }

  async getMatrixAssessments() {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();

    const { data } = await this.httpClient({
      method: 'get',
      baseURL: `${clientApiURL.origin}${clientApiURL.pathname}`,
      url: 'assessments/matrix',
    });

    return data;
  }

  async getAssessmentList(pagination) {
    const config = container.get<Config>(Config.diToken);
    const { apiURL } = config.get();

    const { data } = await this.httpClient({
      method: 'get',
      baseURL: `${apiURL.origin}${apiURL.pathname}`,
      url: `clinician/not-assigned-assessments?Page=${pagination.page}&PageSize=${pagination.pageSize}`,
    });

    return data;
  }

  async addContent(brainFunctionId: number, body: BrainFunctionAddData) {
    this.abortController = new AbortController();

    try {
      const res = await this.httpClient.post(
        this.getURL(`brain-functions/${brainFunctionId}/content`),
        body,
        {
          headers: {
            'content-type': 'multipart/form-data',
          },
          signal: this.abortController.signal,
        },
      );

      const { data } = res;

      return data;
    } catch (e) {
      if (!Axios.isCancel(e)) {
        throw e;
      }
    }
  }

  async deleteContent(brainFunctionId: number, body: BrainFunctionDeleteData) {
    const { data } = await this.httpClient.delete(
      this.getURL(`brain-functions/${brainFunctionId}/content`),
      { data: body },
    );

    return data;
  }

  async updateContent(brainFunctionId: number, body: BrainFunctionUpdateData) {
    const { data } = await this.httpClient.put(
      this.getURL(`brain-functions/${brainFunctionId}/content`),
      body,
    );

    return data;
  }

  async getContent(brainFunctionId: number) {
    const { data } = await this.httpClient.get<BrainFunctionContent[]>(
      this.getURL(`brain-functions/${brainFunctionId}/content`),
    );

    return data;
  }
}
